import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import axios from 'axios';
import {
  LivroCustomizadoService,
  urlDoAsset,
  type Crianca,
  type LivroHeroBook,
  type ManifestoBuilder,
  type Presentation,
  type Trait,
  type Tracos,
  type VarianteBuilder,
} from '../../services/livro-customizado.service';
import TurnstileWidget from '../../components/TurnstileWidget';
import { SKIP_TURNSTILE } from '../../config/features';
import {
  gravarRascunho,
  lerRascunho,
  limparRascunhoSalvo,
  MSG_RASCUNHO_VENCIDO,
  rascunhoVencido,
  type Etapa,
  type LivroPronto,
  type Rascunho,
} from './rascunho';

const TRAITS: Trait[] = ['skin', 'hairStyle', 'hairColor', 'eyes', 'outfit', 'shoes', 'accessory'];

const ABAS: { trait: Trait; titulo: string }[] = [
  { trait: 'skin', titulo: 'Pele' },
  { trait: 'hairStyle', titulo: 'Cabelo' },
  { trait: 'eyes', titulo: 'Olhos' },
  { trait: 'outfit', titulo: 'Roupa' },
  { trait: 'shoes', titulo: 'Calçado' },
  { trait: 'accessory', titulo: 'Acessório' },
];

const PRESENTATIONS: { valor: Presentation; titulo: string }[] = [
  { valor: 'feminine', titulo: 'Menina' },
  { valor: 'masculine', titulo: 'Menino' },
];

const ETAPAS: { id: Etapa; titulo: string }[] = [
  { id: 'livro', titulo: 'Livro' },
  { id: 'personagem', titulo: 'Personagem' },
  { id: 'nome', titulo: 'Nome' },
  { id: 'previa', titulo: 'Prévia' },
];

function erroDaApi(err: unknown): {
  mensagem: string;
  reason?: string;
  campos?: string[];
} {
  if (axios.isAxiosError(err)) {
    if (!err.response)
      return {
        mensagem: 'Sem resposta do servidor. Confira sua conexão e tente de novo.',
      };
    const d = err.response.data as { message?: unknown; reason?: string; issues?: { field?: string }[] } | undefined;
    const campos = (d?.issues ?? []).map((i) => i.field).filter((c): c is string => !!c);
    const msg = typeof d?.message === 'string' && d.message ? d.message : 'Não foi possível salvar. Tente de novo.';
    return { mensagem: msg, reason: d?.reason, campos };
  }
  return { mensagem: 'Algo deu errado. Tente de novo.' };
}

const doPresentation = (p: Presentation) => (v: VarianteBuilder) =>
  v.presentations.length === 0 || v.presentations.includes(p);

/** "Regata · Azul" → grupo "Regata", cor "Azul". */
function agrupar(vs: VarianteBuilder[]): { nome: string; itens: VarianteBuilder[] }[] {
  const grupos = new Map<string, VarianteBuilder[]>();
  for (const v of vs) {
    const nome = v.label.split(' · ')[0];
    grupos.set(nome, [...(grupos.get(nome) ?? []), v]);
  }
  return [...grupos.entries()].map(([nome, itens]) => ({ nome, itens }));
}

const sufixo = (v: VarianteBuilder | undefined) => v?.label.split(' · ')[1] ?? '';

function coresDoCabelo(m: ManifestoBuilder, hairStyle: string): VarianteBuilder[] {
  const estilo = m.variantes.hairStyle?.find((v) => v.id === hairStyle);
  if (estilo?.pigmented) return [];
  const permitidas = m.compatibilidade[hairStyle];
  const cores = m.variantes.hairColor ?? [];
  return permitidas ? cores.filter((c) => permitidas.includes(c.id)) : cores;
}

/** Ajusta a cor do cabelo quando o penteado novo não aceita a atual. */
function normalizarTracos(m: ManifestoBuilder, t: Tracos): Tracos {
  const cores = coresDoCabelo(m, t.hairStyle);
  if (cores.length && !cores.some((c) => c.id === t.hairColor)) return { ...t, hairColor: cores[0].id };
  return t;
}

function tracosPadrao(m: ManifestoBuilder, p: Presentation, base?: Partial<Tracos>): Tracos {
  const padrao = (m.padroes[p] ?? {}) as Partial<Tracos>;
  const t = Object.fromEntries(TRAITS.map((k) => [k, base?.[k] ?? padrao[k] ?? 'none'])) as Tracos;
  return normalizarTracos(m, t);
}

// ------------------------------------------------------------------ avatar

function Avatar({
  manifesto,
  tracos,
  className = '',
}: {
  manifesto: ManifestoBuilder;
  tracos: Tracos;
  className?: string;
}) {
  const [w, h] = manifesto.canvas;
  const camadas = manifesto.ordemCamadas
    .map((id) => {
      const trait = (manifesto.camadas.find((c) => c.id === id)?.trait ?? id) as Trait;
      const v = manifesto.variantes[trait]?.find((x) => x.id === tracos[trait]);
      if (!v) return null;
      const caminho = trait === 'hairStyle' ? (v.previewPorCor?.[tracos.hairColor] ?? v.preview) : v.preview;
      return urlDoAsset(manifesto, caminho);
    })
    .filter((u): u is string => !!u);
  return (
    <div className={`relative mx-auto ${className}`} style={{ aspectRatio: `${w} / ${h}` }}>
      {camadas.map((src) => (
        <img
          key={src}
          src={src}
          alt=""
          draggable={false}
          className="absolute inset-0 w-full h-full object-contain select-none"
        />
      ))}
    </div>
  );
}

// ----------------------------------------------------------------- seletor

function Seletor({
  manifesto,
  trait,
  presentation,
  tracos,
  onEscolher,
}: {
  manifesto: ManifestoBuilder;
  trait: Trait;
  presentation: Presentation;
  tracos: Tracos;
  onEscolher: (patch: Partial<Tracos>) => void;
}) {
  const opcoes = (manifesto.variantes[trait] ?? []).filter(doPresentation(presentation));
  const grupos = agrupar(opcoes);
  const atual = opcoes.find((v) => v.id === tracos[trait]);
  const grupoAtual = grupos.find((g) => g.itens.some((v) => v.id === tracos[trait]));

  const escolherGrupo = (g: { itens: VarianteBuilder[] }) => {
    const mesmaCor = g.itens.find((v) => sufixo(v) && sufixo(v) === sufixo(atual));
    onEscolher({ [trait]: (mesmaCor ?? g.itens[0]).id });
  };

  const cores = trait === 'hairStyle' ? coresDoCabelo(manifesto, tracos.hairStyle) : [];

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
        {grupos.map((g) => {
          const v = g.itens[0];
          const thumb = urlDoAsset(manifesto, v.thumb);
          const ativo = grupoAtual?.nome === g.nome;
          return (
            <button
              key={g.nome}
              type="button"
              onClick={() => escolherGrupo(g)}
              className={`flex flex-col items-center gap-1 rounded-xl border p-1.5 text-[11px] leading-tight text-[#595D69] transition-colors ${
                ativo ? 'border-[#018ED5] bg-[#F5FAFF]' : 'border-[#e6e9f0] bg-white'
              }`}
              aria-pressed={ativo}
            >
              {thumb ? (
                <img src={thumb} alt="" loading="lazy" className="w-full aspect-square object-contain" />
              ) : (
                <span
                  className="w-full aspect-square rounded-lg flex items-center justify-center bg-[#F9F9FB]"
                  style={v.swatch ? { background: v.swatch } : undefined}
                >
                  {!v.swatch && '—'}
                </span>
              )}
              <span className="line-clamp-2 text-center">{g.nome}</span>
            </button>
          );
        })}
      </div>

      {grupoAtual && grupoAtual.itens.length > 1 && (
        <Cores
          itens={grupoAtual.itens}
          selecionado={tracos[trait]}
          rotulo={(v) => sufixo(v) || v.label}
          onEscolher={(id) => onEscolher({ [trait]: id })}
        />
      )}

      {trait === 'hairStyle' && cores.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-[#595D69] mb-1">Cor do cabelo</p>
          <Cores
            itens={cores}
            selecionado={tracos.hairColor}
            rotulo={(v) => v.label}
            onEscolher={(id) => onEscolher({ hairColor: id })}
          />
        </div>
      )}
    </div>
  );
}

function Cores({
  itens,
  selecionado,
  rotulo,
  onEscolher,
}: {
  itens: VarianteBuilder[];
  selecionado: string;
  rotulo: (v: VarianteBuilder) => string;
  onEscolher: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {itens.map((v) => {
        const ativo = v.id === selecionado;
        return (
          <button
            key={v.id}
            type="button"
            title={rotulo(v)}
            aria-label={rotulo(v)}
            aria-pressed={ativo}
            onClick={() => onEscolher(v.id)}
            className={`w-8 h-8 rounded-full border-2 ${ativo ? 'border-[#018ED5] ring-2 ring-[#DBECFF]' : 'border-white shadow'}`}
            style={{ background: v.swatch ?? '#e6e9f0' }}
          />
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------------ prévia

function PreviaLivro({
  draftId,
  draftKey,
  onVencido,
}: {
  draftId: string;
  draftKey: string;
  /** 410: rascunho vencido no Hero Book — o builder volta ao passo 1. */
  onVencido: () => void;
}) {
  const [paginas, setPaginas] = useState<string[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    let vivo = true;
    const urls: string[] = [];
    setPaginas(null);
    setErro(null);
    (async () => {
      try {
        const indices = await LivroCustomizadoService.previa(draftId, draftKey);
        for (const i of indices) {
          const url = await LivroCustomizadoService.paginaPrevia(draftId, draftKey, i);
          urls.push(url);
          if (vivo) setPaginas([...urls]);
        }
        if (vivo && indices.length === 0) setPaginas([]);
      } catch (e) {
        if (!vivo) return;
        if (rascunhoVencido(e)) onVencido();
        else setErro(erroDaApi(e).mensagem);
      }
    })();
    return () => {
      vivo = false;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
    // onVencido muda a cada render do builder e não deve refazer a prévia.
  }, [draftId, draftKey, tentativa]); // eslint-disable-line react-hooks/exhaustive-deps

  if (erro) {
    return (
      <div className="text-center">
        <p className="text-sm text-[#D93025] bg-[#FDECEC] rounded-lg px-3 py-2">{erro}</p>
        <button
          type="button"
          onClick={() => setTentativa((t) => t + 1)}
          className="mt-3 text-sm font-semibold text-[#018ED5]"
        >
          Tentar de novo
        </button>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      {(paginas ?? []).map((src, i) => (
        <img
          key={src}
          src={src}
          alt={`Página ${i + 1} da prévia`}
          className="w-full rounded-lg border border-[#e6e9f0] bg-white"
        />
      ))}
      {paginas === null && (
        <div className="w-full aspect-[2200/2900] rounded-lg bg-[#F9F9FB] animate-pulse flex items-center justify-center text-sm text-[#A0A3AD]">
          Gerando a prévia…
        </div>
      )}
      {paginas !== null && paginas.length === 0 && (
        <p className="text-sm text-[#595D69] text-center">Prévia indisponível para este livro.</p>
      )}
    </div>
  );
}

// ----------------------------------------------------------------- builder

const botaoPrimario = 'w-full h-12 rounded-lg bg-[#018ED5] text-white font-semibold disabled:opacity-50';
const botaoSecundario = 'w-full h-12 rounded-lg border border-[#dadae5] text-[#595D69] font-semibold';
const campo = 'border border-[#dadae5] w-full px-4 rounded-lg h-12 text-[#2C2F3A]';

/**
 * Monta o livro no Hero Book antes do pagamento: livro → personagem → nome →
 * prévia. O rascunho fica salvo no navegador (retoma por até 6 dias). Rascunho
 * vencido lá (410) → limpa o salvo e volta ao passo 1.
 */
export default function BuilderLivro({
  onPronto,
  aviso = null,
}: {
  onPronto: (livro: LivroPronto) => void;
  /** Mensagem ao abrir (ex.: o pagamento achou o rascunho vencido). */
  aviso?: string | null;
}) {
  const [livros, setLivros] = useState<LivroHeroBook[] | null>(null);
  const [rascunho, setRascunho] = useState<Rascunho | null>(() => lerRascunho());
  const [manifesto, setManifesto] = useState<ManifestoBuilder | null>(null);
  const [etapa, setEtapa] = useState<Etapa>(() => lerRascunho()?.etapa ?? 'livro');
  const [livroEscolhido, setLivroEscolhido] = useState<LivroHeroBook | null>(null);
  const [presentation, setPresentation] = useState<Presentation>('feminine');
  const [aba, setAba] = useState<Trait>('skin');
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(aviso);
  const [crianca, setCrianca] = useState<Crianca>(
    () =>
      lerRascunho()?.crianca ?? {
        displayName: '',
        grammaticalGender: 'f',
        dedication: '',
        senderName: '',
      },
  );
  const topoRef = useRef<HTMLDivElement>(null);
  // Cada personagem abre um rascunho no Hero Book: Turnstile antes de criar.
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileKey, setTurnstileKey] = useState(0);
  const exigeTurnstile = !SKIP_TURNSTILE;

  useEffect(() => {
    LivroCustomizadoService.livros()
      .then((l) => {
        setLivros(l);
        if (l.length === 1) setLivroEscolhido(l[0]);
      })
      .catch(() => setErro('Não foi possível carregar os livros. Recarregue a página.'));
  }, []);

  const pack = rascunho?.livro.assets ?? livroEscolhido?.assets ?? null;
  useEffect(() => {
    if (!pack) return;
    LivroCustomizadoService.manifesto(pack.packId, pack.version)
      .then(setManifesto)
      .catch(() => setErro('Não foi possível carregar as opções do personagem. Recarregue a página.'));
  }, [pack?.packId, pack?.version]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    gravarRascunho(rascunho ? { ...rascunho, etapa } : null);
  }, [rascunho, etapa]);

  const irPara = (e: Etapa) => {
    setErro(null);
    setEtapa(e);
    topoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const recomecar = () => {
    setRascunho(null);
    setManifesto(null);
    setCrianca({
      displayName: '',
      grammaticalGender: 'f',
      dedication: '',
      senderName: '',
    });
    irPara('livro');
  };

  /** Rascunho vencido no Hero Book: some do navegador e o builder volta ao passo 1. */
  const recomecarVencido = () => {
    limparRascunhoSalvo();
    recomecar();
    setErro(MSG_RASCUNHO_VENCIDO);
  };

  const tratarErro = (e: unknown) => {
    if (rascunhoVencido(e)) {
      recomecarVencido();
      return;
    }
    const { mensagem, reason, campos } = erroDaApi(e);
    if (reason === 'rascunho_invalido') {
      recomecar();
      setErro(mensagem);
      return;
    }
    const nomes = (campos ?? []).map(
      (c) => ABAS.find((a) => a.trait === c)?.titulo ?? (c === 'hairColor' ? 'Cor do cabelo' : c),
    );
    setErro(nomes.length ? `${mensagem} (${nomes.join(', ')})` : mensagem);
  };

  async function comecar() {
    if (!livroEscolhido) return;
    setOcupado(true);
    setErro(null);
    try {
      const r = await LivroCustomizadoService.criarPersonagem(
        livroEscolhido.bookId,
        presentation,
        turnstileToken || undefined,
      );
      const { presentation: p, ...tracos } = r.character;
      setRascunho({
        draftId: r.draftId,
        draftKey: r.draftKey,
        livro: livroEscolhido,
        presentation: p ?? presentation,
        tracos: Object.fromEntries(TRAITS.map((k) => [k, (tracos as Tracos)[k]])) as Tracos,
        crianca: null,
        etapa: 'personagem',
        salvoEm: Date.now(),
      });
      setCrianca((c) => ({
        ...c,
        grammaticalGender: presentation === 'masculine' ? 'm' : 'f',
      }));
      irPara('personagem');
    } catch (e) {
      tratarErro(e);
    } finally {
      // Token de uso único: a próxima tentativa remonta o widget.
      setTurnstileToken('');
      setTurnstileKey((k) => k + 1);
      setOcupado(false);
    }
  }

  const mudarTracos = (patch: Partial<Tracos>) => {
    if (!rascunho || !manifesto) return;
    setErro(null);
    setRascunho({
      ...rascunho,
      tracos: normalizarTracos(manifesto, { ...rascunho.tracos, ...patch }),
    });
  };

  const mudarPresentation = (p: Presentation) => {
    if (!rascunho || !manifesto || p === rascunho.presentation) return;
    setRascunho({
      ...rascunho,
      presentation: p,
      tracos: tracosPadrao(manifesto, p),
    });
    setCrianca((c) => ({
      ...c,
      grammaticalGender: p === 'masculine' ? 'm' : p === 'feminine' ? 'f' : c.grammaticalGender,
    }));
  };

  async function salvarPersonagem() {
    if (!rascunho) return;
    setOcupado(true);
    setErro(null);
    try {
      await LivroCustomizadoService.gravarTracos(rascunho.draftId, rascunho.draftKey, {
        presentation: rascunho.presentation,
        ...rascunho.tracos,
      });
      irPara('nome');
    } catch (e) {
      tratarErro(e);
    } finally {
      setOcupado(false);
    }
  }

  const nomeValido = /^\p{L}[\p{L} '’-]{0,29}$/u.test(crianca.displayName.trim().replace(/\s+/g, ' '));
  const remetenteValido = !crianca.senderName?.trim() || crianca.senderName.trim().length >= 2;

  async function salvarNome() {
    if (!rascunho || !nomeValido || !remetenteValido) return;
    setOcupado(true);
    setErro(null);
    const limpo: Crianca = {
      displayName: crianca.displayName.trim().replace(/\s+/g, ' '),
      grammaticalGender: crianca.grammaticalGender,
      ...(crianca.dedication?.trim() ? { dedication: crianca.dedication.trim() } : {}),
      ...(crianca.senderName?.trim() ? { senderName: crianca.senderName.trim() } : {}),
    };
    try {
      await LivroCustomizadoService.gravarCrianca(rascunho.draftId, rascunho.draftKey, limpo);
      setRascunho({ ...rascunho, crianca: limpo });
      irPara('previa');
    } catch (e) {
      tratarErro(e);
    } finally {
      setOcupado(false);
    }
  }

  const indiceEtapa = ETAPAS.findIndex((e) => e.id === etapa);
  const tituloEtapa = useMemo(() => ETAPAS[indiceEtapa]?.titulo, [indiceEtapa]);

  const cartao = (conteudo: ReactNode) => (
    <section className="bg-white rounded-2xl border border-[#e6e9f0] p-4 lg:p-6 flex flex-col gap-4">
      {conteudo}
    </section>
  );

  return (
    <div ref={topoRef} className="flex flex-col gap-4 scroll-mt-4">
      <ol className="flex items-center gap-2" aria-label="Etapas">
        {ETAPAS.map((e, i) => (
          <li key={e.id} className="flex-1 flex flex-col gap-1">
            <span className={`h-1.5 rounded-full ${i <= indiceEtapa ? 'bg-[#018ED5]' : 'bg-[#e6e9f0]'}`} />
            <span className={`text-[11px] ${i === indiceEtapa ? 'text-[#018ED5] font-semibold' : 'text-[#A0A3AD]'}`}>
              {e.titulo}
            </span>
          </li>
        ))}
      </ol>
      <span className="sr-only" aria-live="polite">
        Etapa {indiceEtapa + 1} de {ETAPAS.length}: {tituloEtapa}
      </span>

      {erro && <p className="text-sm text-[#D93025] bg-[#FDECEC] rounded-lg px-3 py-2">{erro}</p>}

      {etapa === 'livro' &&
        cartao(
          <>
            <h2 className="text-lg font-bold text-[#2C2F3A]">Escolha o livro</h2>
            {livros === null && !erro && <p className="text-sm text-[#595D69]">Carregando…</p>}
            <div className="flex flex-col gap-2">
              {(livros ?? []).map((l) => {
                const ativo = livroEscolhido?.bookId === l.bookId;
                return (
                  <button
                    key={l.bookId}
                    type="button"
                    onClick={() => setLivroEscolhido(l)}
                    aria-pressed={ativo}
                    className={`text-left rounded-xl border p-3 ${ativo ? 'border-[#018ED5] bg-[#F5FAFF]' : 'border-[#e6e9f0]'}`}
                  >
                    <p className="font-semibold text-[#2C2F3A]">{l.title}</p>
                    <p className="text-xs text-[#595D69]">
                      {l.pageCount} páginas{l.format ? ` · ${l.format}` : ''}
                      {l.finish === 'capa_dura' ? ' · capa dura' : ''}
                    </p>
                  </button>
                );
              })}
            </div>
            <div>
              <p className="text-sm font-semibold text-[#595D69] mb-2">O livro é para</p>
              <div className="flex gap-2">
                {PRESENTATIONS.map((p) => (
                  <button
                    key={p.valor}
                    type="button"
                    onClick={() => setPresentation(p.valor)}
                    aria-pressed={presentation === p.valor}
                    className={`flex-1 h-11 rounded-lg border font-semibold ${
                      presentation === p.valor
                        ? 'border-[#018ED5] bg-[#F5FAFF] text-[#018ED5]'
                        : 'border-[#dadae5] text-[#595D69]'
                    }`}
                  >
                    {p.titulo}
                  </button>
                ))}
              </div>
            </div>
            {exigeTurnstile && (
              <TurnstileWidget
                key={turnstileKey}
                onVerify={setTurnstileToken}
                onExpire={() => setTurnstileToken('')}
                onError={() => setTurnstileToken('')}
              />
            )}
            <button
              type="button"
              disabled={!livroEscolhido || ocupado || (exigeTurnstile && !turnstileToken)}
              onClick={comecar}
              className={botaoPrimario}
            >
              {ocupado ? 'Preparando…' : 'Montar o personagem'}
            </button>
          </>,
        )}

      {etapa === 'personagem' &&
        rascunho &&
        cartao(
          !manifesto ? (
            <p className="text-sm text-[#595D69]">Carregando opções…</p>
          ) : (
            <>
              <div className="flex gap-2">
                {PRESENTATIONS.map((p) => (
                  <button
                    key={p.valor}
                    type="button"
                    onClick={() => mudarPresentation(p.valor)}
                    aria-pressed={rascunho.presentation === p.valor}
                    className={`flex-1 h-9 rounded-lg border text-sm font-semibold ${
                      rascunho.presentation === p.valor
                        ? 'border-[#018ED5] bg-[#F5FAFF] text-[#018ED5]'
                        : 'border-[#dadae5] text-[#595D69]'
                    }`}
                  >
                    {p.titulo}
                  </button>
                ))}
              </div>
              <div className="rounded-xl bg-gradient-to-b from-[#F5FAFF] to-white">
                <Avatar manifesto={manifesto} tracos={rascunho.tracos} className="w-48 sm:w-56" />
              </div>
              <div className="flex gap-1 overflow-x-auto -mx-1 px-1 pb-1" role="tablist">
                {ABAS.map((a) => (
                  <button
                    key={a.trait}
                    type="button"
                    role="tab"
                    aria-selected={aba === a.trait}
                    onClick={() => setAba(a.trait)}
                    className={`shrink-0 px-3 h-9 rounded-full text-sm font-semibold ${
                      aba === a.trait ? 'bg-[#018ED5] text-white' : 'bg-[#F9F9FB] text-[#595D69]'
                    }`}
                  >
                    {a.titulo}
                  </button>
                ))}
              </div>
              <Seletor
                manifesto={manifesto}
                trait={aba}
                presentation={rascunho.presentation}
                tracos={rascunho.tracos}
                onEscolher={mudarTracos}
              />
              <button type="button" disabled={ocupado} onClick={salvarPersonagem} className={botaoPrimario}>
                {ocupado ? 'Salvando…' : 'Continuar'}
              </button>
              <button type="button" onClick={recomecar} className="text-sm text-[#A0A3AD]">
                Trocar de livro
              </button>
            </>
          ),
        )}

      {etapa === 'nome' &&
        rascunho &&
        cartao(
          <>
            <h2 className="text-lg font-bold text-[#2C2F3A]">Nome no livro</h2>
            <label className="flex flex-col gap-1">
              <span className="text-sm font-semibold text-[#595D69]">Nome da criança</span>
              <input
                className={campo}
                maxLength={30}
                value={crianca.displayName}
                onChange={(e) => setCrianca({ ...crianca, displayName: e.target.value })}
                placeholder="Como aparece na história"
              />
              {crianca.displayName && !nomeValido && (
                <span className="text-xs text-[#D93025]">Só letras e espaços, até 30 caracteres.</span>
              )}
            </label>
            <div>
              <p className="text-sm font-semibold text-[#595D69] mb-2">Como o texto se refere à criança</p>
              <div className="flex gap-2">
                {(
                  [
                    ['f', 'Ela'],
                    ['m', 'Ele'],
                    ['name_only', 'Só o nome'],
                  ] as const
                ).map(([valor, titulo]) => (
                  <button
                    key={valor}
                    type="button"
                    onClick={() => setCrianca({ ...crianca, grammaticalGender: valor })}
                    aria-pressed={crianca.grammaticalGender === valor}
                    className={`flex-1 h-10 rounded-lg border text-sm font-semibold ${
                      crianca.grammaticalGender === valor
                        ? 'border-[#018ED5] bg-[#F5FAFF] text-[#018ED5]'
                        : 'border-[#dadae5] text-[#595D69]'
                    }`}
                  >
                    {titulo}
                  </button>
                ))}
              </div>
            </div>
            <label className="flex flex-col gap-1">
              <span className="text-sm font-semibold text-[#595D69]">Dedicatória (opcional)</span>
              <textarea
                className="border border-[#dadae5] w-full px-4 py-3 rounded-lg text-[#2C2F3A] min-h-24"
                maxLength={280}
                value={crianca.dedication ?? ''}
                onChange={(e) => setCrianca({ ...crianca, dedication: e.target.value })}
                placeholder="Uma mensagem carinhosa no começo do livro"
              />
              <span className="text-xs text-[#A0A3AD] self-end">{(crianca.dedication ?? '').length}/280</span>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-sm font-semibold text-[#595D69]">Quem está dando o livro (opcional)</span>
              <input
                className={campo}
                maxLength={40}
                value={crianca.senderName ?? ''}
                onChange={(e) => setCrianca({ ...crianca, senderName: e.target.value })}
                placeholder="Ex.: Vovó Maria"
              />
              {!remetenteValido && <span className="text-xs text-[#D93025]">Pelo menos 2 letras.</span>}
            </label>
            <button
              type="button"
              disabled={ocupado || !nomeValido || !remetenteValido}
              onClick={salvarNome}
              className={botaoPrimario}
            >
              {ocupado ? 'Salvando…' : 'Ver a prévia'}
            </button>
            <button type="button" onClick={() => irPara('personagem')} className={botaoSecundario}>
              Voltar ao personagem
            </button>
          </>,
        )}

      {etapa === 'previa' &&
        rascunho &&
        cartao(
          <>
            <h2 className="text-lg font-bold text-[#2C2F3A]">Prévia do livro</h2>
            <p className="text-sm text-[#595D69]">
              {rascunho.livro.title} — com {rascunho.crianca?.displayName}. As primeiras páginas, como vão ficar.
            </p>
            {manifesto && <Avatar manifesto={manifesto} tracos={rascunho.tracos} className="w-28" />}
            <PreviaLivro draftId={rascunho.draftId} draftKey={rascunho.draftKey} onVencido={recomecarVencido} />
            <button
              type="button"
              onClick={() =>
                onPronto({
                  draftId: rascunho.draftId,
                  draftKey: rascunho.draftKey,
                  bookId: rascunho.livro.bookId,
                  titulo: rascunho.livro.title,
                  nomeCrianca: rascunho.crianca?.displayName ?? '',
                })
              }
              className="w-full h-12 rounded-lg bg-[#5BB947] text-white font-semibold"
            >
              Gostei, ir para o pagamento
            </button>
            <div className="flex gap-2">
              <button type="button" onClick={() => irPara('personagem')} className={botaoSecundario}>
                Ajustar personagem
              </button>
              <button type="button" onClick={() => irPara('nome')} className={botaoSecundario}>
                Ajustar nome
              </button>
            </div>
          </>,
        )}

      {etapa !== 'livro' &&
        !rascunho &&
        cartao(
          <button type="button" onClick={recomecar} className={botaoPrimario}>
            Começar
          </button>,
        )}
    </div>
  );
}
